import React, { useState, useEffect, useCallback } from "react";
import { Link, Routes, Route as RouterRoute, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { cargoAPI, jetAPI, bookingAPI, statsAPI, authAPI } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import NotificationBell from "@/components/NotificationBell";
import OnlineToggle from "@/components/OnlineToggle";
import EarningsWallet from "@/components/partner/EarningsWallet";
import DashboardOverview from "@/components/partner/DashboardOverview";
import MyListings from "@/components/partner/MyListings";
import PartnerBookings from "@/components/partner/PartnerBookings";
import ReviewUsers from "@/components/partner/ReviewUsers";
import NotificationSettings from "@/components/shared/NotificationSettings";
import AddListing from "@/components/partner/AddListing";
import MySharedRoutes from "@/components/partner/MySharedRoutes";
import LocalDeliveries from "@/components/partner/LocalDeliveries";
import PartnerVerification from "@/components/partner/PartnerVerification";
import PartnerAnalytics from "@/components/partner/PartnerAnalytics";
import { 
  Package, 
  Plus,
  Truck, 
  Train, 
  Plane, 
  Ship,
  Calendar as CalendarIcon,
  LogOut,
  LayoutDashboard,
  List,
  BookOpen,
  User,
  Loader2,
  Trash2,
  ArrowRight,
  Users,
  RefreshCw,
  Bike,
  Car,
  MapPin,
  Route as RouteIcon,
  Clock,
  Navigation,
  CheckCircle,
  XCircle,
  Phone,
  IndianRupee,
  Menu,
  X,
  Wallet,
  CreditCard,
  Building2,
  TrendingUp,
  ArrowDownToLine,
  History,
  Star,
  Bell,
  Settings,
  Shield,
  BarChart3
} from "lucide-react";

const transportIcons = {
  road: Truck,
  rail: Train,
  air: Plane,
  water: Ship,
};

const indianCities = [
  "Mumbai", "Delhi", "Bangalore", "Chennai", "Kolkata", "Hyderabad", 
  "Pune", "Ahmedabad", "Jaipur", "Surat", "Lucknow", "Kanpur",
  "Nagpur", "Indore", "Thane", "Bhopal", "Goa", "Chandigarh"
];

const vehicleTypes = {
  road: ["Truck", "Container", "Mini Truck", "Trailer"],
  rail: ["Container Wagon", "Flat Wagon", "Box Wagon"],
  air: ["Cargo Plane", "Passenger Cargo"],
  water: ["Container Ship", "Bulk Carrier", "Tanker", "Barge"],
};

const jetModels = [
  "Cessna Citation", "Gulfstream G650", "Bombardier Global", 
  "Dassault Falcon", "Embraer Phenom", "HondaJet Elite"
];

const amenitiesList = ["WiFi", "Bar", "Catering", "Entertainment", "Bedroom", "Conference Room"];

// ==================== LOCAL DELIVERY PARTNER COMPONENTS ====================

// Main Partner Dashboard Component
export default function PartnerDashboard() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [switchingRole, setSwitchingRole] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loadingVerification, setLoadingVerification] = useState(true);

  // Check verification status on mount
  useEffect(() => {
    const checkVerification = async () => {
      try {
        const response = await api.get("/partner-verification/status");
        setVerificationStatus(response.data);
      } catch (error) {
        // If API fails, assume not verified
        setVerificationStatus({ status: "not_started" });
      } finally {
        setLoadingVerification(false);
      }
    };
    checkVerification();
  }, []);

  const isVerified = verificationStatus?.verification_status === "verified";
  const isPendingVerification = verificationStatus?.verification_status === "pending";
  const isRejected = verificationStatus?.verification_status === "rejected";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleSwitchToUser = async () => {
    setSwitchingRole(true);
    try {
      const response = await authAPI.switchRole();
      toast.success("Switched to Shipper account!");
      login(response.data);
      window.location.href = "/dashboard";
    } catch {
      toast.error("Failed to switch role. Please try again.");
      setSwitchingRole(false);
    }
  };

  // Navigation items - restrict for unverified partners
  const allNavItems = [
    { path: "/partner", label: "Dashboard", icon: LayoutDashboard, requiresVerification: true },
    { path: "/partner/analytics", label: "Analytics", icon: BarChart3, requiresVerification: true },
    { path: "/partner/verification", label: "Verification", icon: Shield, requiresVerification: false },
    { path: "/partner/earnings", label: "Earnings", icon: Wallet, requiresVerification: true },
    { path: "/partner/listings", label: "My Listings", icon: List, requiresVerification: true },
    { path: "/partner/add", label: "Add Listing", icon: Plus, requiresVerification: true },
    { path: "/partner/bookings", label: "Bookings", icon: BookOpen, requiresVerification: true },
    { path: "/partner/local-routes", label: "Local Routes", icon: RouteIcon, requiresVerification: true },
    { path: "/partner/local-deliveries", label: "Local Deliveries", icon: Bike, requiresVerification: true },
    { path: "/partner/reviews", label: "Review Users", icon: Star, requiresVerification: true },
    { path: "/partner/notifications", label: "Notifications", icon: Bell, requiresVerification: false },
  ];

  // Filter nav items based on verification status
  const navItems = isVerified 
    ? allNavItems 
    : allNavItems.filter(item => !item.requiresVerification);

  // Show loading while checking verification
  if (loadingVerification) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // If not verified, redirect to verification page (except if already there)
  if (!isVerified && !location.pathname.includes('/verification') && !location.pathname.includes('/notifications')) {
    return (
      <div className="min-h-screen bg-background" data-testid="partner-dashboard">
        {/* Header */}
        <header className="sticky top-0 z-50 border-b border-border bg-background">
          <div className="flex items-center justify-between h-16 px-4 md:px-6">
            <Link to="/" className="flex items-center gap-2 font-black text-xl tracking-tighter">
              <span className="text-accent">Setu</span>
              <span>xpress</span>
            </Link>
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2">
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </header>

        {/* Verification Required Message */}
        <main className="container max-w-2xl mx-auto px-4 py-12">
          <div className="border border-border p-8 text-center" data-testid="verification-required">
            <Shield className="w-16 h-16 mx-auto text-amber-500 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Verification Required</h1>
            <p className="text-muted-foreground mb-6">
              {isPendingVerification 
                ? "Your verification documents are being reviewed. Please wait for admin approval."
                : isRejected
                  ? "Your verification was rejected. Please re-upload the required documents."
                  : "Complete your verification to access all partner features."}
            </p>
            
            {isPendingVerification && (
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 mb-6 text-left">
                <div className="flex items-center gap-2 text-amber-600 font-medium mb-2">
                  <Clock className="w-5 h-5" />
                  Verification Pending
                </div>
                <p className="text-sm text-muted-foreground">
                  Our team is reviewing your documents. This usually takes 24-48 hours.
                  You'll be notified once approved.
                </p>
              </div>
            )}

            {isRejected && (
              <div className="bg-red-500/10 border border-red-500/30 p-4 mb-6 text-left">
                <div className="flex items-center gap-2 text-red-600 font-medium mb-2">
                  <XCircle className="w-5 h-5" />
                  Verification Rejected
                </div>
                <p className="text-sm text-muted-foreground">
                  {verificationStatus?.rejection_reason || "Please re-upload your documents and try again."}
                </p>
              </div>
            )}

            <Button onClick={() => navigate('/partner/verification')} className="rounded-none gap-2">
              <Shield className="w-4 h-4" />
              {isPendingVerification ? "View Status" : "Start Verification"}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="partner-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          {/* Mobile menu button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
            
          </Link>
          
          <div className="flex items-center gap-2 md:gap-4">
            <NotificationBell />
            <div className="text-right hidden md:block">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.company_name || 'Partner'}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Online Toggle Bar */}
      <OnlineToggle />

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed left-0 top-16 bottom-0 w-72 bg-background border-r border-border p-4 overflow-y-auto">
            {/* User Info */}
            <div className="pb-4 mb-4 border-b border-border">
              <p className="font-medium">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.company_name || 'Partner'}</p>
            </div>
            
            {/* Navigation */}
            <nav className="space-y-1 mb-6">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            
            {/* Switch to Shipper */}
            <div className="pt-4 border-t border-border">
              <Button
                variant="outline"
                className="w-full rounded-none"
                onClick={handleSwitchToUser}
                disabled={switchingRole}
              >
                {switchingRole ? "Switching..." : "Switch to Shipper"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:block w-64 border-r border-border min-h-[calc(100vh-64px-52px)] p-4">
          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-secondary'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Switch to Shipper */}
          <div className="mt-8 p-4 border border-accent/30 bg-accent/5">
            <h3 className="font-bold text-sm mb-2">Need to ship cargo?</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Switch to Shipper mode to book cargo space and private jets.
            </p>
            <Button 
              onClick={handleSwitchToUser}
              disabled={switchingRole}
              variant="outline"
              className="w-full rounded-none"
              data-testid="switch-to-user-btn"
            >
              {switchingRole ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Switch to Shipper
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-8">
          <Routes>
            <RouterRoute index element={<DashboardOverview />} />
            <RouterRoute path="analytics" element={<PartnerAnalytics />} />
            <RouterRoute path="verification" element={<PartnerVerification />} />
            <RouterRoute path="earnings" element={<EarningsWallet />} />
            <RouterRoute path="listings" element={<MyListings />} />
            <RouterRoute path="add" element={<AddListing />} />
            <RouterRoute path="bookings" element={<PartnerBookings />} />
            <RouterRoute path="local-routes" element={<MySharedRoutes />} />
            <RouterRoute path="local-deliveries" element={<LocalDeliveries />} />
            <RouterRoute path="reviews" element={<ReviewUsers />} />
            <RouterRoute path="notifications" element={<NotificationSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
